/**
 * Copy for the "Price on request" price-slot badge shown on browse/search/
 * wishlist accommodation and service cards (and the mobile price bar) for
 * listings that have no bookable price — plus the shared
 * `ByRequestRequestForm`'s field labels, submit/loading/error copy, and
 * success confirmation.
 *
 * Deliberately a distinct module from `constants/packageCopy.ts`, which
 * owns the package purchase panel's package-specific copy (its priced CTA,
 * field labels, notes). This module owns the by-request surfaces for
 * accommodations and services AND the wording every cart-adding CTA shares
 * regardless of listing kind (`ADD_TO_CART_LABEL`, the by-request
 * accessible-name suffix, `BY_REQUEST_PRICE_SLOT_LABEL`) — the package
 * panel imports those shared strings from here so the three kinds can never
 * drift on the one action they have in common. The `BY_REQUEST_LABEL`
 * string value matches `packageCopy.ts::BY_REQUEST_LABEL` for visual
 * consistency across surfaces; `packageCopy.ts` does not import from here.
 */

export const BY_REQUEST_LABEL = 'By Request';

// Field labels — REQUEST_DATE_LABEL fronts single-date mode (a service
// request); REQUEST_CHECK_IN_LABEL / REQUEST_CHECK_OUT_LABEL front
// date-range mode (an accommodation request). REQUEST_GUESTS_LABEL is the
// party-size stepper's default label — callers may override it per listing
// kind (e.g. "Party size" for a service).
export const REQUEST_DATE_LABEL = 'Date';
export const REQUEST_CHECK_IN_LABEL = 'Check-in';
export const REQUEST_CHECK_OUT_LABEL = 'Check-out';
export const REQUEST_GUESTS_LABEL = 'Guests';
export const REQUEST_NOTES_LABEL = 'Notes (optional)';
export const REQUEST_NOTES_PLACEHOLDER = 'Anything we should know for your quote?';
// Shared "Price on request" affordance string for every price-less UI slot a
// by-request modifier touches: the guest renderer's stepper helper (in
// place of a "$0.00 · per booking" price line) and a host booking card's
// total slot when a by-request modifier is selected. One constant so
// both surfaces read identically and an edit to the wording only has to
// land in one place.
export const BY_REQUEST_PRICE_SLOT_LABEL = 'Price on request';

// The one add-to-cart wording every cart-adding CTA renders — the priced
// `CartButton`, the by-request listing/modifier forks, the package purchase
// panel and the mobile sticky-bar trigger — so a guest never sees two
// phrasings (or two casings) of the same action. A by-request add carries
// the accessible-name suffix so the enabled copy says explicitly that no
// price exists yet, rather than silently reading like a priced add.
export const ADD_TO_CART_LABEL = 'Add to Cart';
export const BY_REQUEST_ADD_TO_CART_ARIA_SUFFIX = ' — price provided by quote';

// Admin authoring copy — the "by request" toggle shown in the accommodation
// and service create forms and the edit form, plus its explanatory helper.
export const BY_REQUEST_TOGGLE_LABEL = 'Available by request';
export const BY_REQUEST_TOGGLE_HELPER =
  'No fixed price — customers submit a request and receive a custom quote.';

// Admin authoring copy — the optional "From price" dollars input shown
// directly under the by-request toggle above, in the accommodation and
// service admin forms (single-unit/single-option only). Disabled and
// cleared whenever the toggle above it is off.
export const BY_REQUEST_FROM_PRICE_LABEL = 'From price (optional)';
export const BY_REQUEST_FROM_PRICE_HELPER =
  'Shown to guests as "From $X" — the final price is still set in their quote.';

// Guest-facing copy for the `ByRequestFromPrice` renderer, shown wherever a
// top-level by-request listing carries the admin-typed from-price above:
// the shared `ByRequestRequestForm` header slot, the mobile accommodation
// collapsed bar, and both admin previews (which mirror the rail). Shown AS
// TYPED — no fee-inclusive maths and no "incl. fees & taxes" note, unlike
// `priceDisclosureCopy.ts::INCLUSIVE_FEES_NOTE`, since the figure is
// already the all-in estimate the admin chose to type (ADR-0081).
export const BY_REQUEST_FROM_PRICE_ESTIMATE_NOTE = 'Estimate — final price confirmed in your quote';

// Cart, cart-page and checkout-rail copy for a by-request line's distinct
// "no charge yet" treatment. CHARGED_NOW_BY_REQUEST_NOTE explains why the
// by-request line is excluded from the cart's "Total" figure, and stands
// alone — in place of a misleading "$0" total — when every line in the
// cart is by-request. AWAITING_QUOTE_RAIL_CHARGE_NOTE is the checkout
// rail's charge-note copy for an all-by-request cart. The group heading and
// per-line chip wording ("Quote to follow") live in
// `chargeTimingCopy.ts::QUOTE_TO_FOLLOW_LABEL`, shared with the checkout
// rail chip so the wording can never drift between the two modules.
export const CHARGED_NOW_BY_REQUEST_NOTE =
  'By-request items are quoted separately — nothing is charged for them today.';
export const AWAITING_QUOTE_RAIL_CHARGE_NOTE =
  'A concierge will send a quote for you to approve — nothing is charged today.';

// Cart drawer / cart page "Extras" line — names a cart line's selected
// add-ons (see `CartItemExtra`), shared by both surfaces so the joined
// copy (label, quantity, and by-request suffix) can never drift between
// them.
export const CART_LINE_EXTRAS_LABEL = 'Extras';
export const CART_LINE_EXTRA_BY_REQUEST_SUFFIX = ' (by request)';

// Fallback headings for a booking request whose `listing_name` could not be
// resolved (the underlying listing has since been removed) — kind-derived
// from which leg id the request carries, via `deriveRequestFallbackLabel`.
// Shared between `MyRequestsPage`'s card heading and
// `RequestsSubmittedCard`'s per-request heading so a wording edit lands in
// one place across both surfaces.
export const ACCOMMODATION_REQUEST_FALLBACK_LABEL = 'Accommodation request';
export const SERVICE_REQUEST_FALLBACK_LABEL = 'Service request';
export const PACKAGE_REQUEST_FALLBACK_LABEL = 'Package request';

// Confirmation-page copy for a checkout outcome that produced at least one
// by-request line. REQUESTS_SUBMITTED_HEADING/_BODY front both
// `RequestsSubmittedCard`'s own heading/body and the page-level
// `ConfirmationHero` shown when a checkout produced only requests (no
// priced bookings) — the two surfaces say the same thing in that case, so
// one pair of constants keeps a copy edit from drifting between them.
// REQUESTS_SUBMITTED_CTA_LABEL fronts the card's control back to
// `/requests`, where the guest tracks each request's quote.
export const REQUESTS_SUBMITTED_HEADING = 'Requests submitted';
export const REQUESTS_SUBMITTED_BODY =
  "We'll review each request and email you a quote to approve. Nothing is charged until you approve a quote.";
export const REQUESTS_SUBMITTED_CTA_LABEL = 'Track in My Requests';

// CheckoutForm's "Awaiting confirmation" notice (rendered whenever the cart
// carries a Belhop listing and needs no off-session consent) becomes
// by-request-aware once at least one cart line is awaiting a quote — the
// unmodified confirmation copy would misdescribe a request as something a
// concierge "confirms availability" for, when a request is instead settled
// by an admin-issued quote. Mirrors the notice's existing single-item vs.
// multi-item cart dual copy.
export const AWAITING_QUOTE_NOTICE_SINGLE =
  'This is a by-request listing — a concierge will send you a quote to approve before anything is charged.';
export const AWAITING_QUOTE_NOTICE_MULTI =
  'Your cart includes a request — a concierge will send a quote to approve before anything is charged for it.';

// CheckoutForm's submit CTA label for a cart made entirely of by-request
// lines — the sibling of the card-on-file and payment-mode CTA labels in
// `constants/checkoutFlowCopy.ts`, naming the card-less submit this shape
// actually takes.
export const SUBMIT_REQUESTS_CTA_LABEL = 'Submit requests';

// Per-line results-panel copy for a by-request line that a card-less
// checkout created successfully — the sibling of the priced-line "booked —
// reference #<id>" copy, pointed at My Requests (where the guest tracks the
// quote) rather than a booking reference, since a request carries no
// booking id.
export const CART_RESULT_REQUEST_SUBMITTED_TEXT = 'request submitted — track in My Requests';

// The mobile stepped service-booking sheet's own by-request body copy —
// shown muted at the top of both the Date and Guests steps so the guest
// understands, before picking anything, that no charge happens until a
// quote is approved. Distinct from `BY_REQUEST_TOGGLE_HELPER` (admin
// authoring copy) and `AWAITING_QUOTE_RAIL_CHARGE_NOTE` (checkout rail
// copy) — this is the guest-facing sentence inside the booking sheet
// itself.
export const BY_REQUEST_NO_CHARGE_NOTE =
  "No fixed price. Tell us your dates and we'll send a quote you can approve; nothing is charged now.";
